import { ApiData, Entity, EntityId } from 'entities-next/common'

import { UserGroup } from '../userGroup'

export type Corporate = Entity & CorporateWithSeats

export type CommonCorporateData = {
  readonly name: string
  readonly status: CorporateStatus
  readonly demo: boolean
  readonly logoUrl?: string
  readonly websiteUrl?: string
  readonly adminNotes?: string
  readonly enabledFeatures?: Feature[]
  readonly userGroup?: UserGroup
  readonly userGroupId?: EntityId
  readonly seatsLimit: number
}

export type CorporateWithSeats = {
  readonly seatsUsed?: number
} & CommonCorporateData

export type IdIndexedCorporate = {
  readonly [corporateId: number]: Corporate
}

export enum CorporateStatus {
  active = 'active',
  inactive = 'inactive',
  pov = 'pov',
  trial = 'trial',
}

export enum Feature {
  scoutingRadar = 'scoutingRadar',
  smartAnalyticsReport = 'smartAnalyticsReport',
  crmIntegration = 'crmIntegration',
  dataExport = 'dataExport',
  marketInsightsDashboard = 'marketInsightsDashboard',
  expandedSearch = 'expandedSearch',
  similarCompanies = 'similarCompanies',
  aiCompanyCopilot = 'aiCompanyCopilot',
  people = 'people',
  companyFinancials = 'companyFinancials',
  companyTracker = 'companyTracker',
  patentSearch = 'patentSearch',
  spaceResearch = 'spaceResearch',
  aiColumn = 'aiColumn',
  companyFundingRounds = 'companyFundingRounds',
}

export type ApiCorporate = Corporate

export type ApiCorporateData = ApiData<ApiCorporate>

export const CORPORATE_CACHE_STORE_ID = 'corporate'

export const FeatureToFeatureLabels: {
  [key in Feature]: string
} = {
  [Feature.scoutingRadar]: 'Radar Mode',
  [Feature.smartAnalyticsReport]: 'Smart Report',
  [Feature.crmIntegration]: 'CRM Integration',
  [Feature.dataExport]: 'Company Data Export',
  [Feature.marketInsightsDashboard]: 'Smart Analytics Dashboard',
  [Feature.expandedSearch]: 'Expanded Search',
  [Feature.similarCompanies]: 'Similar Companies',
  [Feature.aiCompanyCopilot]: 'AI Company Copilot',
  [Feature.people]: 'Team Tab',
  [Feature.companyFinancials]: 'Financials Tab',
  [Feature.companyTracker]: 'Company Tracker',
  [Feature.patentSearch]: 'Patents',
  [Feature.spaceResearch]: 'Space Research',
  [Feature.aiColumn]: 'AI Column',
  [Feature.companyFundingRounds]: 'Funding Tab',
} as const

const FeatureLabelToFeature: {
  [key in typeof FeatureToFeatureLabels[Feature]]: Feature
} = Object.keys(FeatureToFeatureLabels).reduce((map, key) => {
  const value = FeatureToFeatureLabels[key as keyof typeof FeatureToFeatureLabels]
  map[value] = key as Feature
  return map
}, {} as { [key in typeof FeatureToFeatureLabels[Feature]]: Feature })

export function featureLabelToFeature(feature: typeof FeatureToFeatureLabels[Feature]): Feature {
  return FeatureLabelToFeature[feature] ?? feature
}

export function featureToFeatureLabel(feature: Feature): typeof FeatureToFeatureLabels[Feature] {
  return FeatureToFeatureLabels[feature] ?? feature
}
